import {useCallback, useEffect, useRef, useState} from 'react';

/** Spec § 5.6: the one fast refresh inside the window. */
export const UNLOCKING_FAST_REFRESH_MS = 3000;
/** Spec § 5.6: how long the screen shows "Unlocking your offer…". */
export const UNLOCKING_WINDOW_MS = 8000;

type UnlockingWindowInputs = {
  isEmpty: boolean;
  isFocused: boolean;
  isLoading: boolean;
  refresh: () => void;
};

/**
 * A screen-local window that opens the first time the offer list resolves empty
 * while the screen is focused (spec § 5.6). It makes one refresh at 3000 ms,
 * unless a load is already in flight, and closes at 8000 ms. Blur and unmount
 * clear its timers; refocus restarts it. `reopen()` restarts it on demand.
 *
 * The fast refresh fires at most once per screen mount: a refocus opens a new
 * window without one, because the focus refresh already asks for the list.
 * `reopen()` never schedules one, because its caller (Try again) has just
 * refreshed.
 */
export const useUnlockingWindow = ({
  isEmpty,
  isFocused,
  isLoading,
  refresh,
}: UnlockingWindowInputs): {open: boolean; reopen: () => void} => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  // The timers read these at fire time, not from the render that started them.
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  // Not reset on blur: it spans the whole mount.
  const hasFiredFastRefresh = useRef(false);

  const start = useCallback(
    (withFastRefresh: boolean) => {
      clearTimers();
      setHasStarted(true);
      setIsOpen(true);
      timers.current = [setTimeout(() => setIsOpen(false), UNLOCKING_WINDOW_MS)];
      if (withFastRefresh && !hasFiredFastRefresh.current) {
        timers.current.push(
          setTimeout(() => {
            hasFiredFastRefresh.current = true;
            if (!isLoadingRef.current) refreshRef.current();
          }, UNLOCKING_FAST_REFRESH_MS),
        );
      }
    },
    [clearTimers],
  );
  const reopen = useCallback(() => start(false), [start]);

  useEffect(() => {
    if (!isFocused) {
      clearTimers();
      setHasStarted(false);
      setIsOpen(false);
      return;
    }
    if (isEmpty && !hasStarted) start(true);
  }, [isFocused, isEmpty, hasStarted, start, clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  // The window counts as open on the render where it is about to start, so the
  // screen never shows the not-ready panel for one frame before "Unlocking".
  const open = isFocused && (isOpen || (isEmpty && !hasStarted));

  return {open, reopen};
};
